/**
 * Экран «Меню» — §9.4, `R22` ABC-анализ и `R23` матрица menu engineering,
 * подэтап M6 этапа 4.
 *
 * Отвечает на вопрос «что держать, что продвигать, что убрать» и отвечает
 * внутри категории: сравнивать популярность стейка и кофе бессмысленно
 * (R23). Квадрант выбирается кнопками, по умолчанию — «Собаки»: ради них
 * экран и открывают.
 *
 * Три ABC показываются рядом, а не одним классом: блюдо бывает A по
 * количеству и C по марже, и это расхождение — главное, что здесь можно
 * увидеть (R22).
 *
 * Арифметики здесь нет (§12.1): квадранты, пороги и классы приходят с
 * сервера.
 */

import { useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Button } from '@shell/ui';
import { Loading } from '@shell/states';
import { formatMoney, formatNumber } from '@shell/format';
import { financeApi } from '../api';
import type { MetricView, PeriodParams } from '../api';
import {
  DownloadLink,
  ExplanationModal,
  FreshnessBadge,
  PeriodPicker,
  ScreenError,
  Section,
  useExplanation,
} from '../components';

const QUADRANTS = [
  { code: 'dog', title: '🐕 Собаки', hint: 'берут мало, маржа ниже категории — кандидаты на вывод' },
  { code: 'plowhorse', title: '🐴 Лошадки', hint: 'популярные, маржа ниже категории — цена или порция' },
  { code: 'puzzle', title: '❓ Загадки', hint: 'маржинальные, но берут мало — продвигать' },
  { code: 'star', title: '⭐ Звёзды', hint: 'популярные и маржинальные — держать' },
] as const;

function num(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function pct(value: unknown): string {
  const parsed = num(value);
  return parsed === null ? '—' : `${formatNumber(parsed, 1)} %`;
}

function money(value: unknown): string {
  const parsed = num(value);
  return parsed === null ? '—' : formatMoney(parsed);
}

export function Menu({
  period,
  onPeriodChange,
  onDrill,
}: {
  period: PeriodParams;
  onPeriodChange: (next: PeriodParams) => void;
  onDrill: (filters: { item?: string }) => void;
}) {
  const [quadrant, setQuadrant] = useState<string>('dog');
  const [explained, explain, closeExplanation] = useExplanation();

  const queries = useQueries({
    queries: ['menu.engineering', 'menu.abc'].map((code) => ({
      queryKey: ['finance', 'metric', code, period],
      queryFn: () => financeApi.metric(code, period),
    })),
  });

  if (queries.some((query) => query.isLoading)) return <Loading />;
  const failed = queries.find((query) => query.error);
  if (failed) {
    return (
      <ScreenError
        error={failed.error}
        period={period}
        anchor={period.to ?? null}
        onPeriodChange={onPeriodChange}
        onRetry={() => queries.forEach((query) => query.refetch())}
      />
    );
  }

  const [matrix, abc] = queries.map((query) => query.data as MetricView);
  const abcByCode = new Map(abc.rows.map((row) => [String(row.code), row]));
  const rows = matrix.rows.filter((row) => row.quadrant === quadrant);
  const current = QUADRANTS.find((item) => item.code === quadrant);

  return (
    <div className="screen">
      {matrix.freshness && <FreshnessBadge freshness={matrix.freshness} />}
      <PeriodPicker period={period} anchor={period.to ?? null} onChange={onPeriodChange} />

      {!matrix.reliable && (
        <p className="tile__caveat" role="note">
          Себестоимость известна не для всей выручки — что не вошло, сказано в «Как посчитано».
        </p>
      )}

      <Section
        title="Матрица меню"
        actions={
          <>
            {QUADRANTS.map((item) => (
              <Button
                key={item.code}
                small
                variant={quadrant === item.code ? 'primary' : 'quiet'}
                onClick={() => setQuadrant(item.code)}
              >
                {item.title} · {String(matrix.extras[item.code] ?? 0)}
              </Button>
            ))}
            <Button small variant="quiet" onClick={() => explain(matrix)}>
              Как посчитано
            </Button>
            <DownloadLink href={financeApi.csvUrl('menu.engineering', period)}>CSV</DownloadLink>
          </>
        }
      >
        {current && <p className="faint">{current.hint}. Сравнение — внутри категории меню.</p>}
        {rows.length === 0 ? (
          <p className="faint">Таких блюд за период нет.</p>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Блюдо</th>
                  <th>Категория</th>
                  <th className="num">Продано</th>
                  <th className="num">Доля в категории</th>
                  <th className="num">Маржа %</th>
                  <th className="num">В категории</th>
                  <th className="num">Маржа ₽</th>
                  <th>ABC: шт / ₽ / маржа</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const classes = abcByCode.get(String(row.code));
                  return (
                    <tr key={String(row.code)}>
                      <td>
                        <button
                          type="button"
                          className="linkish"
                          onClick={() => onDrill({ item: String(row.code) })}
                        >
                          {String(row.name)}
                        </button>
                      </td>
                      <td>{String(row.category)}</td>
                      <td className="num">{formatNumber(num(row.quantity) ?? 0, 0)}</td>
                      <td className="num">
                        {pct(row.popularity_pct)}{' '}
                        <span className="faint">(порог {pct(row.popularity_threshold_pct)})</span>
                      </td>
                      <td className="num">{pct(row.margin_pct)}</td>
                      <td className="num">{pct(row.margin_threshold_pct)}</td>
                      <td className="num">{money(row.contribution)}</td>
                      <td>
                        {classes
                          ? `${classes.abc_quantity ?? '—'} / ${classes.abc_revenue ?? '—'} / ${
                              classes.abc_margin ?? '—'
                            }`
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section
        title="ABC-анализ"
        actions={
          <>
            <Button small variant="quiet" onClick={() => explain(abc)}>
              Как посчитано
            </Button>
            <DownloadLink href={financeApi.csvUrl('menu.abc', period)}>CSV</DownloadLink>
          </>
        }
      >
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Класс</th>
                <th className="num">По количеству</th>
                <th className="num">По выручке</th>
                <th className="num">По марже</th>
              </tr>
            </thead>
            <tbody>
              {['A', 'B', 'C'].map((letter) => (
                <tr key={letter}>
                  <td>{letter}</td>
                  <td className="num">{String(abc.extras[`quantity_${letter}`] ?? 0)}</td>
                  <td className="num">{String(abc.extras[`revenue_${letter}`] ?? 0)}</td>
                  <td className="num">{String(abc.extras[`margin_${letter}`] ?? 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="faint chart-hint">
          A — блюда, дающие первые 80 % показателя, B — следующие 15 %, C — остальные. По марже
          — только блюда с известной себестоимостью.
        </p>
      </Section>

      <ExplanationModal metric={explained} onClose={closeExplanation} />
    </div>
  );
}
